import { forceCollide, forceLink, forceSimulation, forceX, forceY, type ForceLink, type Simulation } from 'd3-force'
import type { GraphLinkDatum, GraphNodeDatum } from './graphModel'

/**
 * Radial View as the DSGN-63 frame draws it: every parent is a hub with its
 * leaves bunched round it like a star, the hubs spread over one round
 * cluster, the enterprise in the middle. The numbers, named so the tests and
 * the component read one source.
 */
export const FORCE = {
  /** A hub's first leaf sits this far beyond the hub's own circle... */
  leafBase: 38,
  /** ...and the k-th one √k × this further out: the leaves fill a disk (a sunflower), not one ring. */
  leafStep: 26,
  /** Clear space between a hub's leaf disk and the leaf disk of the hub it hangs from. */
  hubGap: 36,
  hierarchyStrength: 1,
  /** Hubs keep each other's stars apart by the size of their stars (the leaves are kept apart by the collision). */
  hubRepulsion: 0.6,
  collidePadding: 10,
  /** A weak pull to the middle, so the stars settle into one round cluster. */
  centreStrength: 0.02,
  velocityDecay: 0.42,
  alphaDecay: 0.06,
  /** Settled is when alpha falls to this: the seed starts in the final shape, so few ticks are needed. */
  alphaMin: 0.01,
  /** The layout is computed before the first paint (no animation): from a seed already in its shape... */
  settleAlpha: 0.4,
  /** ...or from where the nodes are, after a collapse or an expand. */
  resettleAlpha: 0.4,
} as const

/** A node drawn as a leaf in Radial View: no children, or its children are folded away. */
export const isRadialLeaf = (d: GraphNodeDatum): boolean => d.collapsed || d.childIds.length === 0

/**
 * Where each visible node hangs from its parent, from the visible tree:
 * `dist` is its link length, `cluster` the radius of its own leaf disk.
 */
export interface RadialPlan {
  dist: Map<string, number>
  cluster: Map<string, number>
  leaves: Map<string, number>
  /** The radius of a node's whole sub-tree as planned. */
  extent: Map<string, number>
  /** The Spacing setting as a factor (1 = 100%); every distance above is already multiplied by it. */
  spacing: number
}

/** A node's drawn radius (Radial View draws hubs larger, through `s`). */
const drawnR = (d: GraphNodeDatum): number => d.r * d.s

const leafDiskRadius = (hub: GraphNodeDatum, leaves: number): number =>
  leaves === 0 ? drawnR(hub) : drawnR(hub) + FORCE.leafBase + FORCE.leafStep * Math.sqrt(Math.max(0, leaves - 1))

/**
 * `spacing` (the Spacing setting, 1 = 100%) scales the whole plan: every
 * link length, star and gap grows by it, so the picture at 190% is the one
 * at 100% blown up about the root, with the nodes and labels kept their size.
 */
export const radialPlan = (nodes: GraphNodeDatum[], spacing = 1): RadialPlan => {
  const visible = new Set(nodes.map((d) => d.id))
  const byId = new Map(nodes.map((d) => [d.id, d]))
  const leaves = new Map<string, number>()
  const cluster = new Map<string, number>()
  const dist = new Map<string, number>()
  const extent = new Map<string, number>()
  const kidsOf = (d: GraphNodeDatum) =>
    d.collapsed ? [] : d.childIds.filter((c) => visible.has(c)).map((c) => byId.get(c) as GraphNodeDatum)
  for (const d of nodes) {
    const leafKids = kidsOf(d).filter(isRadialLeaf)
    leaves.set(d.id, leafKids.length)
    cluster.set(d.id, leafDiskRadius(d, leafKids.length))
    leafKids.forEach((c, k) => dist.set(c.id, drawnR(d) + FORCE.leafBase + FORCE.leafStep * Math.sqrt(k)))
  }
  // Hubs hang round their parent on a sunflower too, spaced by the size of
  // their own sub-trees, so many stars fill a disk instead of crowding a ring.
  const visit = (d: GraphNodeDatum): number => {
    const hubs = kidsOf(d).filter((c) => !isRadialLeaf(c))
    let r = cluster.get(d.id) ?? drawnR(d)
    if (hubs.length > 0) {
      const sizes = hubs.map(visit)
      const step = sizes.reduce((sum, x) => sum + 2 * x + FORCE.hubGap, 0) / hubs.length
      const order = hubs.map((h, i) => ({ h, size: sizes[i] })).sort((x, y) => y.size - x.size)
      order.forEach(({ h, size }, k) => {
        const at = (cluster.get(d.id) ?? drawnR(d)) + FORCE.hubGap + step / 2 + step * HUB_SPIRAL * Math.sqrt(k)
        dist.set(h.id, at)
        r = Math.max(r, at + size)
      })
    }
    extent.set(d.id, r)
    return r
  }
  for (const d of nodes) if (!d.parentId || !visible.has(d.parentId)) visit(d)
  // Everything above is linear in the lengths it starts from, so scaling the
  // result is the same as planning with every length scaled.
  if (spacing !== 1) for (const m of [dist, cluster, extent]) m.forEach((v, k) => m.set(k, v * spacing))
  return { dist, cluster, leaves, extent, spacing }
}

/** How tightly a parent's hubs spiral: 1 would space neighbours a full step apart on every turn. */
const HUB_SPIRAL = 0.5

/** A hierarchy link's length, from the plan. */
export const linkDistance = (l: GraphLinkDatum, plan: RadialPlan): number => plan.dist.get(l.target.id) ?? FORCE.leafBase * plan.spacing

/**
 * Only the tree's own links pull. A tag-to-tag connection does not take part
 * in the layout: pulling, it dragged every sheet's equipment across the
 * graph into one hairball, and on a real tenant there are thousands of them.
 */
export const layoutLinks = (links: GraphLinkDatum[]): GraphLinkDatum[] => links.filter((l) => l.hierarchy)

/**
 * Hubs push each other's stars apart: two hubs closer than the sum of their
 * leaf disks (plus the gap) are pushed apart, in proportion to the overlap.
 * Pairwise over the hubs only, so it costs nothing next to the leaves.
 */
export const forceHubRepulsion = (getPlan: () => RadialPlan) => {
  let hubs: GraphNodeDatum[] = []
  const force = (alpha: number) => {
    const plan = getPlan()
    for (let i = 0; i < hubs.length; i++) {
      const a = hubs[i]
      const ra = plan.cluster.get(a.id) ?? a.r
      for (let j = i + 1; j < hubs.length; j++) {
        const b = hubs[j]
        const want = ra + (plan.cluster.get(b.id) ?? b.r) + FORCE.hubGap * plan.spacing
        let dx = b.x + (b.vx ?? 0) - a.x - (a.vx ?? 0)
        let dy = b.y + (b.vy ?? 0) - a.y - (a.vy ?? 0)
        let dist = Math.hypot(dx, dy)
        if (dist >= want) continue
        if (dist < 1e-6) { dx = 1e-3 * (j - i); dy = 0; dist = Math.abs(dx) }
        const push = ((want - dist) / dist) * alpha * FORCE.hubRepulsion * 0.5
        const aFixed = a.fx != null, bFixed = b.fx != null
        const share = aFixed || bFixed ? 2 : 1
        if (!aFixed) { a.vx = (a.vx ?? 0) - dx * push * share; a.vy = (a.vy ?? 0) - dy * push * share }
        if (!bFixed) { b.vx = (b.vx ?? 0) + dx * push * share; b.vy = (b.vy ?? 0) + dy * push * share }
      }
    }
  }
  force.initialize = (nodes: GraphNodeDatum[]) => { hubs = nodes.filter((d) => !isRadialLeaf(d)) }
  return force
}

/** Collide radius follows the animated scale, so shrunken context nodes let neighbours drift closer. */
export const collideRadius = (d: GraphNodeDatum): number => d.r * d.s + FORCE.collidePadding

/**
 * A second, softer collision for the LABEL under each node, so file names do
 * not print over each other. Half the label's width, because a label is wide
 * and short and is drawn centred; the node collision above stays the spec's.
 *
 * It was 75% of that half-width — a compromise for the demo graph, where
 * labels are short and nothing is crowded. On a real tenant (~180 sheets and
 * equipment, 26-character ids) the remaining quarter was exactly the amount
 * two neighbours' labels printed over each other by, ring after ring. The
 * full half-width is what "the labels do not overlap" actually requires.
 */
export const LABEL_CLEARANCE = 1.0
export const labelCollideRadius = (d: GraphNodeDatum): number => Math.max(0, (d.labelWidth / 2) * LABEL_CLEARANCE * d.s)

/** A node's collision radius: its circle, or half its label's width when that is wider. */
export const nodeClearance = (d: GraphNodeDatum): number => Math.max(collideRadius(d), labelCollideRadius(d))

/**
 * The Radial View simulation over the visible set. It is never run as an
 * animation: `settle` ticks it to rest before anything is painted.
 */
export const createSimulation = (nodes: GraphNodeDatum[], links: GraphLinkDatum[], spacing = 1): Simulation<GraphNodeDatum, GraphLinkDatum> => {
  let plan = radialPlan(nodes, spacing)
  const sim = forceSimulation<GraphNodeDatum, GraphLinkDatum>(nodes)
    .force('link', forceLink<GraphNodeDatum, GraphLinkDatum>(layoutLinks(links)).id((d) => d.id).distance((l) => linkDistance(l, plan)).strength(FORCE.hierarchyStrength))
    .force('hubs', forceHubRepulsion(() => plan))
    // One collision for the circle and the label under it: labels do not print over each other.
    .force('collide', forceCollide<GraphNodeDatum>().radius(nodeClearance).iterations(1))
    // No forceCenter: the root is pinned at the origin (seedRadialPositions),
    // and these pull the rest gently toward it so the stars form one cluster.
    // Hubs only: a leaf follows its hub, and pulled to the middle itself it
    // left its hub stranded at the edge of its own star.
    .force('x', forceX<GraphNodeDatum>(0).strength((d) => (isRadialLeaf(d) ? 0 : FORCE.centreStrength)))
    .force('y', forceY<GraphNodeDatum>(0).strength((d) => (isRadialLeaf(d) ? 0 : FORCE.centreStrength)))
    .velocityDecay(FORCE.velocityDecay)
    .alphaDecay(FORCE.alphaDecay)
    .alphaMin(FORCE.alphaMin)
    .stop()
  planRef.set(sim, (p) => { plan = p })
  return sim
}

const planRef = new WeakMap<Simulation<GraphNodeDatum, GraphLinkDatum>, (p: RadialPlan) => void>()

/** Re-read the plan after the visible set or the Spacing setting changed. */
export const replan = (sim: Simulation<GraphNodeDatum, GraphLinkDatum>, links: GraphLinkDatum[], spacing = 1): void => {
  const plan = radialPlan(sim.nodes(), spacing)
  planRef.get(sim)?.(plan)
  sim.force<ForceLink<GraphNodeDatum, GraphLinkDatum>>('link')?.links(layoutLinks(links))
  // Re-run each force's initialize for the new node set (the hub list, the collision radii).
  sim.nodes(sim.nodes())
}

/** Tick the simulation to rest at once, from `alpha`. Nothing moves on screen while it runs. */
export const settle = (sim: Simulation<GraphNodeDatum, GraphLinkDatum>, alpha: number): void => {
  sim.stop()
  sim.alpha(alpha)
  const ticks = Math.ceil(Math.log(sim.alphaMin() / alpha) / Math.log(1 - sim.alphaDecay()))
  for (let i = 0; i < ticks; i++) sim.tick()
}
